import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  MessageSquare,
  Loader2,
  Copy,
  AlertCircle,
  FileText,
  Clock,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import {
  Button,
  Input,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Alert,
  AlertDescription,
  Badge,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  useAuth,
  kmsService,
} from '@aaas/shared';
import type { Citation } from '@aaas/shared';

interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'error';
  content: string;
  timestamp: Date;
  citations?: Citation[];
  processingTime?: number;
}

interface ChatInterfaceProps {
  spaceId: string;
  spaceName: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ spaceId, spaceName }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestedQuestions, setSuggestedQuestions] = useState<string[]>([]);
  const [, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { idToken } = useAuth();

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load suggested questions on component mount
  useEffect(() => {
    loadSuggestedQuestions();
  }, [spaceId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSuggestedQuestions = async () => {
    try {
      if (!idToken) return;

      const response = await kmsService.getSuggestedQuestions(idToken, spaceId);
      setSuggestedQuestions(response.data || []);
    } catch {
      // Silently fail - suggested questions are optional
      setSuggestedQuestions([]);
    }
  };

  const handleAskQuestion = async (question: string) => {
    if (!question.trim() || isLoading || !idToken) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString() + '_user',
      type: 'user',
      content: question,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setCurrentQuestion('');
    setIsLoading(true);
    setError(null);

    try {
      // Stateless request - no session tracking
      const response = await kmsService.askQuestion(idToken, spaceId, question);

      // Add AI response with citations
      const aiMessage: ChatMessage = {
        id: Date.now().toString() + '_ai',
        type: 'assistant',
        content: response.answer,
        citations: response.citations || [],
        processingTime: response.processing_time_ms,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (err: unknown) {
      // Handle chat errors gracefully
      let errorDetail: string | undefined;

      if (typeof err === 'object' && err !== null && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        errorDetail = axiosError.response?.data?.detail;
      }

      // Add error message
      const errorMessage: ChatMessage = {
        id: Date.now().toString() + '_error',
        type: 'error',
        content:
          errorDetail ||
          'Sorry, I encountered an error processing your question. Please try again.',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
      setError(errorDetail || 'Failed to get response');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    handleAskQuestion(currentQuestion);
  };

  const handleSuggestedQuestion = (question: string) => {
    handleAskQuestion(question);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Citation Dialog Component
  const CitationDialog: React.FC<{ citations: Citation[] }> = ({ citations }) => {
    const [open, setOpen] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState<string | null>(null);
    const [expandedDocs, setExpandedDocs] = useState<Set<number>>(new Set()); // All documents collapsed by default

    const toggleDocument = (index: number) => {
      setExpandedDocs((prev) => {
        const next = new Set(prev);
        if (next.has(index)) {
          next.delete(index);
        } else {
          next.add(index);
        }
        return next;
      });
    };

    const handleCopySection = (text: string, index: string) => {
      copyToClipboard(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    };

    const totalSections = citations.reduce((acc, c) => acc + c.sections.length, 0);

    return (
      <>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setOpen(true)}
          className="group relative text-xs font-medium bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 border-green-200 hover:from-green-100 hover:to-emerald-100 hover:border-green-300 hover:shadow-sm transition-all duration-200"
        >
          <FileText className="h-3.5 w-3.5 mr-1.5 group-hover:scale-110 transition-transform" />
          View {citations.length} Source{citations.length !== 1 ? 's' : ''}
        </Button>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] p-0 gap-0 overflow-hidden">
            {/* Header with gradient */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-green-50 to-emerald-50">
              <div className="flex items-start justify-between">
                <div className="space-y-1.5">
                  <DialogTitle className="flex items-center gap-3 text-xl">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <FileText className="h-5 w-5 text-green-700" />
                    </div>
                    <span className="bg-gradient-to-r from-green-700 to-emerald-700 bg-clip-text text-transparent">
                      Answer Sources & Citations
                    </span>
                    <Badge className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100 ml-4">
                      AI Verified
                    </Badge>
                  </DialogTitle>
                  <DialogDescription className="text-sm">
                    {totalSections} section{totalSections !== 1 ? 's' : ''} from {citations.length}{' '}
                    document{citations.length !== 1 ? 's' : ''} used to generate this answer
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>

            {/* Scrollable content */}
            <div className="overflow-y-auto px-6 py-5" style={{ maxHeight: 'calc(85vh - 140px)' }}>
              <div className="space-y-5">
                {citations.map((citation, citationIndex) => {
                  const isExpanded = expandedDocs.has(citationIndex);

                  return (
                    <div
                      key={citationIndex}
                      // className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-all duration-200"
                      className="py-4"
                    >
                      {/* Document Header - Clickable */}
                      <button
                        onClick={() => toggleDocument(citationIndex)}
                        className="w-full bg-gradient-to-r from-gray-50 to-gray-100/50 px-5 py-3 rounded-lg border border-gray-200 hover:from-green-50 hover:to-emerald-50/50 transition-colors duration-200"
                      >
                        <div className="flex items-center justify-between px-2">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-white rounded-lg shadow-sm">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-green-600 transition-transform" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-gray-500 transition-transform" />
                              )}
                            </div>
                            <div className="p-2 bg-white rounded-lg shadow-sm">
                              <FileText className="h-4 w-4 text-green-600" />
                            </div>
                            <div className="text-left">
                              <h3 className="font-semibold text-gray-900 text-sm">
                                {citation.document_name}
                              </h3>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {citation.sections.length} referenced section
                                {citation.sections.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs bg-white">
                              Document {citationIndex + 1}
                            </Badge>
                          </div>
                        </div>
                      </button>

                      {/* Sections - Collapsible */}
                      {isExpanded && (
                        <div className="mt-4 space-y-3 bg-gradient-to-b from-white to-gray-50/30 animate-in fade-in slide-in-from-top-2 duration-200">
                          {citation.sections.map((section, sectionIndex) => {
                            const sectionKey = `${citationIndex}-${sectionIndex}`;
                            const isCopied = copiedIndex === sectionKey;

                            return (
                              <div
                                key={sectionIndex}
                                className="p-4 group/section relative bg-white rounded-lg border border-gray-200 hover:border-green-200 transition-colors duration-200"
                              >
                                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-green-500 to-emerald-500 rounded-l-lg" />
                                <div className="pl-4 pr-4 py-3.5">
                                  <div className="flex items-start justify-between gap-3 mb-2.5">
                                    <div className="flex items-center gap-2">
                                      <Badge
                                        variant="outline"
                                        className="text-xs font-normal bg-green-50 text-green-700 border-green-200"
                                      >
                                        Section {sectionIndex + 1}/{citation.sections.length}
                                      </Badge>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleCopySection(section.text, sectionKey)}
                                      className="h-7 px-2"
                                    >
                                      <Copy
                                        className={`h-3.5 w-3.5 ${isCopied ? 'text-green-600' : ''}`}
                                      />
                                      <span className="ml-1.5 text-xs">
                                        {isCopied ? 'Copied!' : 'Copy'}
                                      </span>
                                    </Button>
                                  </div>
                                  <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                    {section.text}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
              <p className="text-xs text-gray-500">
                Citations are automatically extracted from your documents
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(false)}
                className="text-xs"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
    if (message.type === 'user') {
      return (
        <div className="flex justify-end mb-4">
          <div className="max-w-3xl bg-blue-600 text-white rounded-lg px-4 py-2">
            <p className="whitespace-pre-wrap">{message.content}</p>
            <div className="text-xs text-blue-100 mt-1">
              {message.timestamp.toLocaleTimeString()}
            </div>
          </div>
        </div>
      );
    }

    if (message.type === 'error') {
      return (
        <div className="flex justify-start mb-4">
          <div className="max-w-3xl">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{message.content}</AlertDescription>
            </Alert>
          </div>
        </div>
      );
    }

    // Assistant message
    return (
      <div className="flex justify-start mb-6">
        <div className="max-w-4xl w-full">
          <div className="bg-gray-50 rounded-lg border">
            {/* Message Header */}
            <div className="flex items-center justify-between p-3 border-b">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-blue-600" />
                </div>
                <span className="font-medium text-gray-900">AI Assistant</span>
                {message.citations && message.citations.length > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs bg-green-50 text-green-700 border-green-200"
                  >
                    {message.citations.length} source{message.citations.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {message.processingTime && (
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {message.processingTime}ms
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(message.content)}
                  className="h-6 w-6 p-0"
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Message Content */}
            <div className="p-4">
              <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed">
                <ReactMarkdown
                  components={{
                    // Customize rendering for better styling
                    p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                    ul: ({ children }) => <ul className="mb-2 pl-4 space-y-1">{children}</ul>,
                    ol: ({ children }) => <ol className="mb-2 pl-4 space-y-1">{children}</ol>,
                    li: ({ children }) => <li className="text-gray-800">{children}</li>,
                    strong: ({ children }) => (
                      <strong className="font-semibold text-gray-900">{children}</strong>
                    ),
                    code: ({ children }) => (
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-gray-800">
                        {children}
                      </code>
                    ),
                    pre: ({ children }) => (
                      <pre className="bg-gray-100 p-2 rounded text-sm overflow-x-auto">
                        {children}
                      </pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700">
                        {children}
                      </blockquote>
                    ),
                    h1: ({ children }) => (
                      <h1 className="text-lg font-bold mb-2 text-gray-900">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-bold mb-2 text-gray-900">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-bold mb-1 text-gray-900">{children}</h3>
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>

              {/* Citations - Answer Quality Indicators */}
              {message.citations && message.citations.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-3">
                    <CitationDialog citations={message.citations} />
                    <Badge
                      variant="outline"
                      className="text-xs bg-green-50 text-green-700 border-green-200"
                    >
                      Quality Verified
                    </Badge>
                  </div>
                </div>
              )}

              {/* Timestamp */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Chat with Documents
        </CardTitle>
        <CardDescription>
          Ask questions about "{spaceName}" and get AI-powered answers with source citations
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col gap-4 min-h-0">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
          {messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-medium mb-2">Start a Conversation</p>
              <p className="text-sm mb-4">
                Ask questions about your documents and get intelligent answers
              </p>

              {/* Suggested Questions */}
              {suggestedQuestions.length > 0 && (
                <div className="mt-6 space-y-2">
                  <p className="text-xs font-medium text-gray-600 mb-3">Try asking:</p>
                  <div className="space-y-2 max-w-md mx-auto">
                    {suggestedQuestions.slice(0, 3).map((question, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleSuggestedQuestion(question)}
                        className="w-full text-left justify-start h-auto py-2 px-3 text-xs"
                        disabled={isLoading}
                      >
                        {question}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {isLoading && (
                <div className="flex items-center gap-2 text-gray-500 py-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Thinking...</span>
                </div>
              )}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t pt-4">
          <form onSubmit={handleSubmitForm} className="flex gap-2">
            <div className="flex-1">
              <Input
                value={currentQuestion}
                onChange={(e) => setCurrentQuestion(e.target.value)}
                placeholder="Ask a question about your documents..."
                disabled={isLoading}
                className="w-full"
                maxLength={2000}
              />
            </div>
            <Button type="submit" disabled={isLoading || !currentQuestion.trim()} className="px-3">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>

          {/* Character count */}
          <div className="flex justify-end items-center mt-2 text-xs text-gray-500">
            <div>{currentQuestion.length}/2000</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatInterface;
